import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { DatabasesRoutes } from '@/modules/main/pages/databases/routes'
import { WorkflowsRoutes } from '@/modules/main/pages/workflows/routes'
import { UsersRoutes } from '@/modules/main/pages/users/routes'
import { AuthRoutes } from '@/modules/auth/routes'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { companyService } from '@/services/company'
import { FormRoutes } from '@/modules/form/routes'
import { CompanyRoutes } from '@/modules/main/pages/company/router'
import { TenantsRoutes } from '@/modules/main/pages/company/tenants/routes'
import { HomeRoutes } from '@/modules/main/pages/home/routes'
import { BusinessIntelligenceRoutes } from '@/modules/main/pages/bi/routes'
import { PortalRoutes } from '@/modules/portal/routes'
import { useAuthCandidateStore } from '@/stores/auth_candidate'
import { ATSRoutes } from '@/modules/main/pages/ats/routes'
import { verifyRoutePermission } from './guard'
import { modalService } from '@/services/utils/modal.service'
import { BiRoutes } from '@/modules/bi/routes'
import { useLoadingStore } from '@/stores/loading'
import { Toast } from '@/utils/toast'

import { WhatsAppRoutes } from '@/modules/main/pages/whatsapp/routes'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'main',
    component: () => import('@/modules/main/Main.vue'),
    beforeEnter: async () => {
      // Carrega os dados do usuário
      await useAuthStore().loadUser();

      // Recarrega a empresa com os dados da rota autenticada (a pública,
      // carregada no boot do App.vue, não traz bl_super_admin) - guards de
      // rota como superAdminOnly dependem desse valor estar pronto aqui.
      // beforeEnter roda a cada navegação dentro de "main" (todo filho passa
      // por este registro de rota), então só busca se ainda não carregou.
      const companyStore = useCompanyStore();
      if (companyStore.company?.bl_super_admin === undefined) {
        const company = await companyService.get();
        companyStore.company = company;

        // Sem isso, logo/logoExpanded/background/video (computeds da store)
        // continuam batendo em /public/company/* sem vc_apelido - a pública
        // cai pro fallback company::default (RH Nossa) sempre, mesmo com a
        // empresa certa já carregada aqui. Só a rota /portal setava
        // nickname antes disso.
        companyStore.nickname = company.vc_apelido;
      }
    },
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/modules/main/pages/home/Index.vue'),
        children: HomeRoutes
      },
      {
        path: 'databases',
        name: 'databases',
        component: () => import('@/modules/main/pages/databases/Index.vue'),
        children: DatabasesRoutes
      },
      {
        path: 'workflows',
        name: 'workflows',
        component: () => import('@/modules/main/pages/workflows/Index.vue'),
        beforeEnter: verifyRoutePermission,
        children: WorkflowsRoutes
      },
      {
        path: 'ats',
        name: 'ats',
        component: () => import('@/modules/main/pages/ats/Index.vue'),
        beforeEnter: verifyRoutePermission,
        meta: { requiresModule: "ats" },
        children: ATSRoutes
      },
      {
        path: 'whatsapp',
        name: 'whatsapp',
        component: () => import('@/modules/main/pages/whatsapp/Index.vue'),
        beforeEnter: verifyRoutePermission,
        meta: { requiresModule: "whatsapp" },
        children: WhatsAppRoutes
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/modules/main/pages/users/Index.vue'),
        beforeEnter: verifyRoutePermission,
        meta: { permission: "user.access" },
        children: UsersRoutes
      },
      {
        path: 'company',
        name: 'company',
        component: () => import('@/modules/main/pages/company/Index.vue'),
        beforeEnter: verifyRoutePermission,
        meta: { permission: "company.access" },
        children: CompanyRoutes
      },
      {
        path: 'company/tenants',
        name: 'tenants',
        component: () => import('@/modules/main/pages/company/tenants/Index.vue'),
        beforeEnter: verifyRoutePermission,
        meta: { permission: "company.manage_tenants", superAdminOnly: true },
        children: TenantsRoutes
      },
      {
        path: 'businessIntelligences',
        name: 'businessIntelligences',
        component: () => import('@/modules/main/pages/bi/Index.vue'),
        beforeEnter: verifyRoutePermission,
        meta: { permission: "bi.access", requiresModule: "bi" },
        children: BusinessIntelligenceRoutes
      },

      {
        path: '/denied',
        name: 'denied',
        component: () => import('@/components/shared/GPermissionDenied.vue'),
      },
    ]
  },
  {
    path: '/',
    name: 'login',
    component: () => import('@/modules/auth/Auth.vue'),
    children: AuthRoutes
  },
  {
    path: '/form',
    name: 'form',
    component: () => import('@/modules/form/Form.vue'),
    children: FormRoutes
  },
  {
    // :nickname opcional - /portal sozinho cai pra company::default no
    // backend (ver CompanyService.GetPublic), /rhnossa/portal mostra o
    // portal daquela empresa especificamente.
    path: '/:nickname?/portal',
    name: 'portal',
    component: () => import('@/modules/portal/Portal.vue'),
    children: PortalRoutes,
    beforeEnter: async (to) => {
      const companyStore = useCompanyStore();
      const nickname = (to.params.nickname as string) ?? '';

      // beforeEnter roda a cada navegação dentro do portal (todo filho
      // passa por este registro de rota) - só busca de novo se ainda não
      // carregou ou se o nickname da URL mudou.
      if (!companyStore.company || companyStore.nickname !== nickname) {
        companyStore.nickname = nickname;
        companyStore.company = await companyService.getInfo(nickname);
      }

      // Portal de vagas depende do módulo "ats" - sem ele a rota nem
      // deveria existir pro visitante, por isso "notFound" e não "denied".
      if (!companyStore.hasModule('ats')) {
        return { name: 'notFound' };
      }

      // Carrega os dados do candidato
      try {
        await useAuthCandidateStore().loadCandidate();
      } catch (error) {

      }
    },
  },
  {
    // :nickname opcional - /businessIntelligence/:code sozinho cai pra
    // company::default no backend (ver PortalTenantScopeMiddleware),
    // /rhnossa/businessIntelligence/:code resolve o dashboard daquela
    // empresa especificamente (mesmo padrão da rota /:nickname/portal).
    path: '/:nickname?/businessIntelligence/:code',
    name: 'businessIntelligenceView',
    component: () => import('@/modules/bi/components/view/BiView.vue'),
    children: BiRoutes,
    beforeEnter: (to) => {
      useCompanyStore().nickname = (to.params.nickname as string) ?? '';
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('@/components/shared/GNotFound.vue'),
  }
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach((to) => {
  if (!to.redirectedFrom) {
    useLoadingStore().startLoading();
  }
})

router.afterEach(() => {
  useLoadingStore().stopLoading();
  modalService.closeAll();
})

router.onError((error) => {
  useLoadingStore().stopLoading();
  Toast.error('Não foi possível carregar a página. Verifique sua conexão e tente novamente.');
  console.error('Erro ao navegar de rota:', error);
})
